<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-root-v-if */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import PlatformAnnouncementsAPI from 'dashboard/api/platformAnnouncements';

const { accountScopedRoute } = useAccount();

const announcements = ref([]);
const activeIndex = ref(0);
const isLoading = ref(true);
const expandedImage = ref(null);

const categoryLabels = {
  promotion: 'Promoção',
  news: 'Notícia',
  tip: 'Dica',
  improvement: 'Melhoria',
  maintenance: 'Manutenção',
};

const categoryClasses = {
  promotion: 'bg-n-amber-3 text-n-amber-11',
  news: 'bg-n-blue-3 text-n-blue-11',
  tip: 'bg-n-teal-3 text-n-teal-11',
  improvement: 'bg-n-violet-3 text-n-violet-11',
  maintenance: 'bg-n-slate-3 text-n-slate-11',
};

const currentAnnouncement = computed(
  () => announcements.value[activeIndex.value]
);
const hasMultipleAnnouncements = computed(() => announcements.value.length > 1);

const formatDate = value =>
  new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

const selectAnnouncement = index => {
  activeIndex.value = index;
};

const showPrevious = () => {
  activeIndex.value =
    (activeIndex.value - 1 + announcements.value.length) %
    announcements.value.length;
};

const showNext = () => {
  activeIndex.value = (activeIndex.value + 1) % announcements.value.length;
};

const markCurrentAsRead = async () => {
  const announcement = currentAnnouncement.value;
  if (!announcement || announcement.read_at) return;

  try {
    await PlatformAnnouncementsAPI.read(announcement.id);
    announcement.read_at = new Date().toISOString();
  } catch {
    // A navegação não deve ser bloqueada quando a confirmação de leitura falhar.
  }
};

const openImage = () => {
  if (!currentAnnouncement.value?.image_url) return;
  expandedImage.value = {
    url: currentAnnouncement.value.image_url,
    alt: currentAnnouncement.value.title,
  };
};

const closeImage = () => {
  expandedImage.value = null;
};

const handleKeydown = event => {
  if (event.key === 'Escape') closeImage();
};

const loadAnnouncements = async () => {
  try {
    const { data } = await PlatformAnnouncementsAPI.get();
    announcements.value = (data.payload || [])
      .filter(item => !item.dismissed_at)
      .slice(0, 3);
  } catch {
    announcements.value = [];
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadAnnouncements();
  window.addEventListener('keydown', handleKeydown);
});
onBeforeUnmount(() => window.removeEventListener('keydown', handleKeydown));
</script>

<template>
  <section
    v-if="isLoading || announcements.length"
    class="overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1"
    aria-label="Comunicados recentes"
  >
    <header
      class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak px-4 py-3 md:px-5"
    >
      <div class="flex items-center gap-3">
        <span
          class="grid size-9 place-items-center rounded-lg bg-n-teal-3 text-n-teal-11"
        >
          <span class="i-lucide-megaphone size-4" />
        </span>
        <div>
          <h2 class="mb-0 text-sm font-semibold text-n-slate-12">
            Comunicados recentes
          </h2>
          <p class="mb-0 text-xs text-n-slate-10">
            As três últimas novidades do sistema
          </p>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <template v-if="hasMultipleAnnouncements">
          <button
            type="button"
            class="grid size-8 place-items-center rounded-lg border border-n-weak text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
            aria-label="Comunicado anterior"
            @click="showPrevious"
          >
            <span class="i-lucide-chevron-left size-4" />
          </button>
          <button
            type="button"
            class="grid size-8 place-items-center rounded-lg border border-n-weak text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
            aria-label="Próximo comunicado"
            @click="showNext"
          >
            <span class="i-lucide-chevron-right size-4" />
          </button>
        </template>
        <router-link
          :to="accountScopedRoute('platform_announcements')"
          class="ml-1 inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium text-n-brand hover:bg-n-alpha-2"
        >
          Ver todos
          <span class="i-lucide-arrow-up-right size-3.5" />
        </router-link>
      </div>
    </header>

    <div v-if="isLoading" class="animate-pulse p-5">
      <div class="mb-3 h-4 w-24 rounded bg-n-alpha-3" />
      <div class="mb-3 h-6 w-1/2 rounded bg-n-alpha-3" />
      <div class="h-4 w-3/4 rounded bg-n-alpha-2" />
    </div>

    <article
      v-else-if="currentAnnouncement"
      :key="currentAnnouncement.id"
      class="grid min-h-40 md:grid-cols-[minmax(0,1fr)_14rem]"
    >
      <div class="flex min-w-0 flex-col justify-between p-5">
        <div>
          <div class="mb-3 flex flex-wrap items-center gap-2">
            <span
              class="rounded-full px-2.5 py-1 text-xs font-medium"
              :class="
                categoryClasses[currentAnnouncement.category] ||
                categoryClasses.maintenance
              "
            >
              {{ categoryLabels[currentAnnouncement.category] || 'Comunicado' }}
            </span>
            <span
              v-if="!currentAnnouncement.read_at"
              class="flex items-center gap-1.5 text-xs font-medium text-n-brand"
            >
              <span class="size-1.5 rounded-full bg-n-brand" />
              Novo
            </span>
            <span class="text-xs text-n-slate-10">
              {{ formatDate(currentAnnouncement.published_at) }}
            </span>
          </div>
          <h3 class="mb-2 text-lg font-semibold text-n-slate-12">
            {{ currentAnnouncement.title }}
          </h3>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div
            v-dompurify-html="currentAnnouncement.body"
            class="line-clamp-3 max-w-3xl text-sm leading-6 text-n-slate-11"
          />
        </div>

        <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div
            v-if="hasMultipleAnnouncements"
            class="flex items-center gap-1.5"
            aria-label="Posição no carrossel"
          >
            <button
              v-for="(_, index) in announcements"
              :key="index"
              type="button"
              class="h-2 rounded-full transition-all"
              :class="
                index === activeIndex
                  ? 'w-6 bg-n-brand'
                  : 'w-2 bg-n-alpha-3 hover:bg-n-slate-8'
              "
              :aria-label="`Exibir comunicado ${index + 1}`"
              :aria-current="index === activeIndex ? 'true' : undefined"
              @click="selectAnnouncement(index)"
            />
          </div>
          <span v-else />

          <a
            v-if="
              currentAnnouncement.action_label && currentAnnouncement.action_url
            "
            :href="currentAnnouncement.action_url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex h-9 items-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:opacity-90"
            @click="markCurrentAsRead"
          >
            {{ currentAnnouncement.action_label }}
            <span class="i-lucide-external-link size-4" />
          </a>
          <router-link
            v-else
            :to="accountScopedRoute('platform_announcements')"
            class="inline-flex h-9 items-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
            @click="markCurrentAsRead"
          >
            Ler comunicado
            <span class="i-lucide-arrow-right size-4" />
          </router-link>
        </div>
      </div>

      <button
        v-if="currentAnnouncement.image_url"
        type="button"
        class="group relative block h-40 overflow-hidden border-t border-n-weak bg-n-alpha-2 text-left md:h-full md:max-h-56 md:border-l md:border-t-0"
        :aria-label="`Ampliar imagem: ${currentAnnouncement.title}`"
        @click="openImage"
      >
        <img
          :src="currentAnnouncement.image_url"
          :alt="currentAnnouncement.title"
          class="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
        />
        <span
          class="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-lg bg-n-slate-12/80 px-2.5 py-1.5 text-xs font-medium text-n-slate-1 opacity-0 shadow-lg backdrop-blur transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        >
          <span class="i-lucide-expand size-3.5" />
          Ampliar
        </span>
      </button>
      <div
        v-else
        class="hidden place-items-center border-l border-n-weak bg-n-alpha-1 text-n-slate-8 md:grid"
      >
        <span class="i-lucide-newspaper size-10" />
      </div>
    </article>

    <Teleport to="body">
      <div
        v-if="expandedImage"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm md:p-8"
        role="dialog"
        aria-modal="true"
        :aria-label="`Imagem ampliada: ${expandedImage.alt}`"
        @click.self="closeImage"
      >
        <button
          type="button"
          class="absolute right-4 top-4 z-10 grid size-11 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white md:right-6 md:top-6"
          aria-label="Fechar imagem ampliada"
          @click="closeImage"
        >
          <span class="i-lucide-x size-6" />
        </button>
        <img
          :src="expandedImage.url"
          :alt="expandedImage.alt"
          class="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
          @click.stop
        />
      </div>
    </Teleport>
  </section>
</template>
